import { cva } from 'class-variance-authority';
import { AlertTriangle, AlertCircle, Info, ChevronRight } from 'lucide-react';

import { Icon } from '@@/Icon';
import { Button } from '@@/buttons';

import { Recommendation, RECOMMENDATION_SEVERITY } from './types';

interface Props {
  recommendation: Recommendation;
}

const containerVariants = cva(
  'flex items-center gap-1 rounded-lg p-4 ![border-style:solid] !border',
  {
    variants: {
      severity: {
        [RECOMMENDATION_SEVERITY.ERROR]:
          '!border-error-4 !bg-error-2 th-dark:!bg-error-10 th-dark:!border-error-8 th-highcontrast:!bg-error-10/60 th-highcontrast:!border-white',
        [RECOMMENDATION_SEVERITY.WARNING]:
          '!border-warning-4 !bg-warning-2 th-dark:!bg-warning-8/25 th-dark:!border-warning-8 th-highcontrast:!bg-warning-8/60 th-highcontrast:!border-white',
        [RECOMMENDATION_SEVERITY.INFO]:
          '!border-blue-4 !bg-blue-2 th-dark:!bg-blue-10/50 th-dark:!border-blue-8 th-highcontrast:!bg-blue-10/60 th-highcontrast:!border-white',
      },
    },
  }
);

const iconWrapVariants = cva(
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-sm',
  {
    variants: {
      severity: {
        [RECOMMENDATION_SEVERITY.ERROR]:
          'bg-error-5/50 th-dark:bg-error-8/50 th-highcontrast:bg-error-8/50',
        [RECOMMENDATION_SEVERITY.WARNING]:
          'bg-warning-5/50 th-dark:bg-warning-8/50 th-highcontrast:bg-warning-8/50',
        [RECOMMENDATION_SEVERITY.INFO]:
          'bg-blue-5/50 th-dark:bg-blue-8/50 th-highcontrast:bg-blue-8/50',
      },
    },
  }
);

const iconColorVariants = cva('text-lg', {
  variants: {
    severity: {
      [RECOMMENDATION_SEVERITY.ERROR]:
        '!text-error-8 th-dark:!text-white th-highcontrast:!text-white',
      [RECOMMENDATION_SEVERITY.WARNING]:
        '!text-warning-8 th-dark:!text-white th-highcontrast:!text-white',
      [RECOMMENDATION_SEVERITY.INFO]:
        '!text-blue-8 th-dark:!text-white th-highcontrast:!text-white',
    },
  },
});

const severityIcons = {
  [RECOMMENDATION_SEVERITY.ERROR]: AlertCircle,
  [RECOMMENDATION_SEVERITY.WARNING]: AlertTriangle,
  [RECOMMENDATION_SEVERITY.INFO]: Info,
};

export function RecommendationItem({ recommendation }: Props) {
  const { severity, title, description, actionUrl, actionLabel } =
    recommendation;
  const IconComponent = severityIcons[severity];

  return (
    <div className={containerVariants({ severity })}>
      <div className={iconWrapVariants({ severity })}>
        <Icon
          icon={IconComponent}
          className={iconColorVariants({ severity })}
        />
      </div>
      <div className="ml-2 flex flex-1 flex-col justify-center">
        <h4 className="m-0 !text-sm font-semibold text-gray-9 th-highcontrast:text-white th-dark:text-white">
          {title}
        </h4>
        {description && (
          <p className="m-0 p-0 !text-xs text-gray-7 th-highcontrast:text-white th-dark:text-white">
            {description}
          </p>
        )}
      </div>

      <Button
        as="a"
        props={{ href: actionUrl }}
        color="default"
        size="small"
        className="!m-0 shrink-0 no-underline"
        data-cy={`recommendation-action-${recommendation.typeId}`}
      >
        {actionLabel}
        <Icon icon={ChevronRight} className="ml-1" />
      </Button>
    </div>
  );
}
